import React from 'react';

/**
 * Attempts to scroll the sentinel element into view and guarantees the scroll container
 * truly reaches the bottom (accounts for async layout shifts like images, dynamic fonts, footer resize).
 */
export function scrollElementIntoViewWithFallback(
  targetEl: HTMLElement | null,
  containerEl?: HTMLElement | null,
  behavior: ScrollBehavior = 'smooth',
  extraRetries: number = 2,
  retryDelayMs: number = 150
) {
  if (!targetEl) return;
  const scopedScroller = targetEl.closest('[data-agent-auto-scroll-container="true"], [data-agent-settings-scroll-container="true"]') as HTMLElement | null;
  const scroller = containerEl || scopedScroller || findScrollParent(targetEl);
  const lockSource = scopedScroller || (scroller as HTMLElement | null);
  const autoScrollLocked = lockSource?.dataset?.agentAutoScrollLock === '1';
  if (autoScrollLocked) {
    return;
  }
  try {
    targetEl.scrollIntoView({ behavior, block: 'end' });
  } catch { /* ignore */ }

  const ensure = (attempt: number) => {
    if (!scroller) return;
    const ensureLocked = (scopedScroller || (scroller as HTMLElement))?.dataset?.agentAutoScrollLock === '1';
    if (ensureLocked) return;
    const remaining = scroller.scrollHeight - (scroller.scrollTop + scroller.clientHeight);
    if (remaining > 4) {
      scroller.scrollTo({ top: scroller.scrollHeight, behavior });
      if (attempt < extraRetries) {
        setTimeout(() => ensure(attempt + 1), retryDelayMs);
      }
    }
  };
  // Use rAF first (after immediate layout), then timed retries.
  requestAnimationFrame(() => ensure(0));
}

/** Find the nearest scrollable ancestor. */
export function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  if (!el) return null;
  let current: HTMLElement | null = el.parentElement;
  while (current) {
    if (current.dataset?.agentAutoScrollContainer === 'true') {
      return current;
    }
    if (current.dataset?.agentSettingsScrollContainer === 'true') {
      return current;
    }
    const style = getComputedStyle(current);
    const overflowY = style.overflowY;
    if (/(auto|scroll)/.test(overflowY)) {
      return current;
    }
    current = current.parentElement;
  }
  return document.scrollingElement as HTMLElement | null;
}

export interface UseScrollBottomOptions {
  /** Distance in px from bottom within which we consider we are at bottom. */
  threshold?: number;
  /** Whether to auto scroll when dependencies change. */
  autoScrollOnDeps?: boolean;
  /** Scroll behavior for auto scrolls. */
  behavior?: ScrollBehavior;
}

/**
 * Hook to manage scroll-to-bottom logic + visibility of a floating button.
 * Provide a containerRef (scrollable element) and a sentinelRef (element at bottom).
 */
export function useScrollBottom(
  containerRef: React.RefObject<HTMLElement>,
  sentinelRef: React.RefObject<HTMLElement>,
  opts: UseScrollBottomOptions = {}
) {
  const { threshold = 60, autoScrollOnDeps = true, behavior = 'smooth' } = opts;
  const [showScrollToBottom, setShowScrollToBottom] = React.useState(false);

  const compute = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const atBottom = scrollHeight - scrollTop - clientHeight < threshold;
    setShowScrollToBottom(!atBottom);
  }, [threshold, containerRef]);

  const scrollToBottom = React.useCallback(() => {
    scrollElementIntoViewWithFallback(sentinelRef.current, containerRef.current, behavior);
  }, [sentinelRef, containerRef, behavior]);

  // Attach scroll listener
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = () => compute();
    el.addEventListener('scroll', handler, { passive: true });
    // Initial computation
    compute();
    return () => el.removeEventListener('scroll', handler);
  }, [compute, containerRef]);

  // Auto scroll when deps change
  React.useEffect(() => {
    if (autoScrollOnDeps) {
      scrollToBottom();
      // Recompute after scroll attempt
      setTimeout(compute, 30);
    }
  }, [autoScrollOnDeps, compute, scrollToBottom]);

  return { showScrollToBottom, scrollToBottom, handleScroll: compute };
}
